import SearchIcon from '@mui/icons-material/SearchOutlined'
import { Box, Button, Checkbox, FormControlLabel, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Dialogs, useDialogs } from '../../../../sharedComponents/src/Contexts/DialogsContext'
import BaseDialog from '../../../../sharedComponents/src/Dialogs/Common/BaseDialog'
import { useScreenSize } from '../../../../sharedComponents/src/hooks/useScreenSize'
import { useStoreActions, useStoreState } from '../../../../store/hooks'
import { ITag } from '../../../PuzzlesView/functions/puzzlesApi'

type GroupedTags = {
    keys: string[]
    groups: { [key: string]: Array<ITag> }
}

const TAGS_LIMIT = 5

const PuzzleTags: React.FC = () => {
    const currentTags = useStoreState((state) => state.puzzlePracticeView.currentTags)
    const allTags = useStoreState((state) => state.puzzlePracticeView.allTags)

    const setCurrentTags = useStoreActions((actions) => actions.puzzlePracticeView.setCurrentTags)

    const [search, setSearch] = useState('')
    const [tagsIDs, setTagsIDs] = useState<number[]>(currentTags)
    const { isMobile } = useScreenSize()
    const { unregisterDialog } = useDialogs()

    const parsedTagsIDs = useMemo(() => {
        return tagsIDs.reduce((acc, tagID) => {
            acc[tagID] = true
            return acc
        }, {})
    }, [tagsIDs])

    useEffect(() => {
        if (!isMobile) {
            setSearch('')
        }
    }, [isMobile])

    const isTagsLimited = useMemo(() => tagsIDs.length >= TAGS_LIMIT, [tagsIDs])

    const groupedTags: GroupedTags = useMemo(() => {
        let groups: GroupedTags['groups'] = {}
        allTags.forEach((tag) => {
            const currentSymbol = tag.friendlyName.charAt(0)
            if (!search || tag.friendlyName.toLocaleLowerCase().includes(search)) {
                if (groups[currentSymbol]) {
                    groups[currentSymbol].push(tag)
                } else {
                    groups[currentSymbol] = [tag]
                }
            }
        })
        const keys = Object.keys(groups)

        return { keys, groups }
    }, [allTags, search])

    const onResetClick = () => {
        setTagsIDs([])
        setCurrentTags([])
        unregisterDialog(Dialogs.PUZZLE_TAGS)
    }

    const onApplyClick = () => {
        setCurrentTags(tagsIDs)
        unregisterDialog(Dialogs.PUZZLE_TAGS)
    }

    const handleCheckbox = (value: number): void => {
        setTagsIDs((prevTags) =>
            prevTags.includes(value) ? prevTags.filter((item) => item !== value) : prevTags.concat([value]),
        )
    }

    return (
        <BaseDialog name={Dialogs.PUZZLE_TAGS} padding="10px" verticalCentered={false}>
            <Stack alignItems="center">
                <Typography variant="h1" fontSize="3.5rem" textTransform="uppercase">
                    Themes
                </Typography>
                <Typography
                    variant="body2"
                    textTransform="uppercase"
                    fontWeight={700}
                    textAlign="center"
                    lineHeight={1.6}
                >{`Select up to ${TAGS_LIMIT} themes to practice specific puzzles.`}</Typography>
            </Stack>
            {isMobile && (
                <TextField
                    sx={{
                        '> div': { padding: '0.5rem' },
                        '.MuiInputAdornment-root': {
                            marginRight: '0.5rem',
                        },
                        input: {
                            fontSize: '14px',
                            padding: '0.5rem 0 ',
                        },
                        svg: {
                            height: '2.5rem',
                            width: '2.5rem',
                        },
                    }}
                    variant="outlined"
                    color="warning"
                    onChange={(e) => setSearch(e.target.value.toLocaleLowerCase())}
                    value={search}
                    placeholder="Find Skill To Improve"
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            )}
            <Box
                sx={{
                    width: '100%',
                    display: 'grid',
                    py: '16px',
                    alignItems: 'flex-start',
                    borderRadius: '0 !important',
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)' },
                    flexGrow: 1,
                    overflow: 'auto',
                    gap: '1rem',
                }}
            >
                {groupedTags.keys.map((key) => (
                    <Box sx={{ userSelect: 'none', width: isMobile ? '100%' : 'auto' }} key={key}>
                        <Typography variant="h3" textTransform="uppercase" sx={{ fontSize: '40px' }}>
                            {key}
                        </Typography>
                        {groupedTags.groups[key].map((tag) => (
                            <FormControlLabel
                                key={tag.id}
                                sx={{
                                    height: isMobile ? '22px' : '32px',
                                    marginRight: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                                value="end"
                                control={
                                    <Checkbox
                                        sx={{ mr: '-0.25rem' }}
                                        disabled={!parsedTagsIDs[tag.id] && isTagsLimited}
                                        size="medium"
                                        checked={Boolean(parsedTagsIDs[tag.id])}
                                        onChange={() => handleCheckbox(tag.id)}
                                    />
                                }
                                labelPlacement="end"
                                label={tag.friendlyName}
                                slotProps={{
                                    typography: {
                                        variant: 'body2',
                                    },
                                }}
                            />
                        ))}
                    </Box>
                ))}
            </Box>

            <Box display="flex" gap="0.5rem" width="100%" justifyContent="center">
                <Button variant="contained" fullWidth size={isMobile ? 'medium' : 'large'} onClick={onResetClick}>
                    Reset
                </Button>
                <Button variant="contained" fullWidth size={isMobile ? 'medium' : 'large'} onClick={onApplyClick}>
                    Apply
                </Button>
            </Box>
        </BaseDialog>
    )
}

PuzzleTags.displayName = 'PuzzleTags'

export default PuzzleTags
